import { useQuery } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import {
    Calculation,
    FinPlansApi,
    LoansGetCalculationsData,
    LoansGetCalculationsParams,
} from '@servicetitan/financing-api-client';
import { AxiosResponse } from 'axios';
import { ServerError } from '@servicetitan/financing-utils-shared';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';

/**
 * Gets the effective monthly payment for a calculation.
 * Uses the lower of defaultMonthlyPayment and suggestedMonthlyPayment (if it exists and is positive).
 */
const getEffectiveMonthlyPayment = (calc: Calculation): number => {
    const { defaultMonthlyPayment, suggestedMonthlyPayment } = calc;

    // If suggestedMonthlyPayment is null, undefined, or not positive, use defaultMonthlyPayment
    if (suggestedMonthlyPayment == null || suggestedMonthlyPayment <= 0) {
        return defaultMonthlyPayment;
    }

    // Return the lower of the two payments
    return Math.min(defaultMonthlyPayment, suggestedMonthlyPayment);
};

/**
 * Sorts calculations by:
 * 1. Effective monthly payment (ascending)
 * 2. Default rate (ascending) - as tie-breaker
 */
const sortCalculations = (calculations: Calculation[]): Calculation[] => {
    return [...calculations].sort((a, b) => {
        const aPayment = getEffectiveMonthlyPayment(a);
        const bPayment = getEffectiveMonthlyPayment(b);

        // Primary sort: by effective monthly payment (lowest first)
        if (aPayment !== bPayment) {
            return aPayment - bPayment;
        }

        // Secondary sort: by default rate (lowest first)
        return a.defaultRate - b.defaultRate;
    });
};

const selectEligiblePlans =
    // The first argument is an array of input selectors, telling it which pieces of the state we want to use
    // In this case we are not selecting from the redux state but from the API query result
    // We also need to use tenantSettings which is not part of the query result, but passed as an additional argument
    (response: AxiosResponse<LoansGetCalculationsData>) => {
        const eligiblePlans = response.data?.filter(calc => calc.eligibleForApplication);
        let minimumAmount: number = 0;
        let noPlansEligible: boolean = false;

        if (eligiblePlans?.length === 0 && response.data?.length > 0) {
            const computedMin = response.data.reduce((min, plan) => {
                const planMin = plan.minLoanAmount;
                return planMin != null && planMin > 0 && planMin < min ? planMin : min;
            }, Infinity);
            if (computedMin !== Infinity) {
                minimumAmount = computedMin;
                noPlansEligible = true;
            }
        }

        // Sort eligible plans by effective monthly payment, then by default rate
        const sortedEligiblePlans = eligiblePlans ? sortCalculations(eligiblePlans) : eligiblePlans;

        return {
            data: sortedEligiblePlans,
            noPlansEligible,
            minimumAmount,
        };
    };

export interface UsePreApprovalCalculationParams {
    preApprovalId: string | null | undefined;
    planNumber: string | null | undefined;
    amount: number;
    tenantId: string | null;
    buId: string | null;
    state?: string;
    /** Gate from usePreApprovalDisplay (gates 1–6); combined with the plan/amount sanity checks. */
    enabled: boolean;
}

/**
 * Targeted calculation for a single pre-approved GreenSky plan at the Desired Financed Amount.
 *
 * Separate from the selector's all-plans query: the pre-approved plan comes from tenant pre-approval
 * settings and need not be in the BU's rules, so a `planList` request bypasses rules/tiers and
 * computes any plan present in GreenSky's catalog. Returns every row for the request (no
 * eligible-only filter) so `usePreApprovalDisplay` can read `eligibleForApplication`/`minLoanAmount`
 * to choose between visible and outOfRange; the consumer narrows to the plan number itself.
 */
export const usePreApprovalCalculation = (params: UsePreApprovalCalculationParams) => {
    const [apiClient] = useDependencies(FinPlansApi);

    return useQuery({
        queryKey: [
            'preApprovalCalculation',
            params.preApprovalId,
            params.planNumber,
            params.amount,
            params.tenantId,
            params.buId,
            params.state,
        ],
        queryFn: () =>
            apiClient.calculationsGetCalculations({
                planList: [params.planNumber!],
                providerName: 'GreenSky',
                amount: params.amount,
                tenantId: params.tenantId,
                buId: params.buId,
                state: params.state,
                merchantId: 'all',
                allPlans: false,
                useAdditionalPromotions: true,
            }),
        enabled:
            params.enabled &&
            !!params.planNumber &&
            Number.isFinite(params.amount) &&
            params.amount > 0,
        throwOnError: false,
        // Return all rows; narrow by plan number in usePreApprovalDisplay (avoids a stale closure
        // over params.planNumber inside select).
        select: resp => resp.data,
    });
};

// Simple wrapper to get calculations and filter out ineligible plans based on tenant settings
export const useGetEligiblePlans = (params: LoansGetCalculationsParams) => {
    const [apiClient] = useDependencies(FinPlansApi);
    const { isValidLoanAmount } = useLoanAmountValidation(params.amount);

    return useQuery<
        AxiosResponse<LoansGetCalculationsData>,
        ServerError,
        {
            data: Calculation[];
            noPlansEligible: boolean;
            minimumAmount: number;
        },
        [string, LoansGetCalculationsParams]
    >({
        queryKey: ['loansGetCalculations', params],
        queryFn: () => apiClient.calculationsGetCalculations(params),
        select: selectEligiblePlans,
        enabled: isValidLoanAmount,
    });
};

/**
 * Keeps the selector's unfiltered response separate so existing consumers retain eligible-only plans.
 */
export const useGetPlanCalculations = (params: LoansGetCalculationsParams) => {
    const [apiClient] = useDependencies(FinPlansApi);
    const { isValidLoanAmount } = useLoanAmountValidation(params.amount);

    return useQuery<AxiosResponse<LoansGetCalculationsData>, ServerError>({
        queryKey: ['loansGetCalculations', params],
        queryFn: () => apiClient.calculationsGetCalculations(params),
        enabled: isValidLoanAmount,
    });
};
